import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Search, Menu, X, Heart, User, LogOut } from 'lucide-react';
import { useCartStore } from '../../lib/cartStore';
import { useAuthStore } from '../../lib/authStore';
import { logout } from '../../lib/api';
import BrandName from '../BrandName';
import toast from 'react-hot-toast';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const navigate = useNavigate();
  const itemCount = useCartStore((s) => s.getItemCount());
  const { user, isAuthenticated, clearUser } = useAuthStore();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/produtos?search=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
      setMobileMenuOpen(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (e) {}
    clearUser();
    setUserMenuOpen(false);
    toast.success('Até logo!');
    navigate('/');
  };

  useEffect(() => {
    const close = () => setUserMenuOpen(false);
    if (userMenuOpen) document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [userMenuOpen]);

  return (
    <header data-testid="main-header" className="sticky top-0 z-50 bg-[#09090B]/95 backdrop-blur-md border-b-2 border-red-600">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center hover:opacity-80 transition-opacity" data-testid="logo-link">
            <img src="/motoveloz_logo_semfundo.png" alt="MOTOVELOZ" className="h-16 md:h-24 w-auto object-contain" />
          </Link>

          <nav className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-white hover:text-red-500 transition-colors font-semibold text-sm uppercase tracking-wider" data-testid="nav-home">Início</Link>
            <Link to="/produtos" className="text-white hover:text-red-500 transition-colors font-semibold text-sm uppercase tracking-wider" data-testid="nav-products">Produtos</Link>
            <Link to="/produtos?promocao=true" className="text-white hover:text-red-500 transition-colors font-semibold text-sm uppercase tracking-wider" data-testid="nav-promos">Promoções</Link>
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <form onSubmit={handleSearch} className="hidden lg:block">
              <div className="relative">
                <input type="text" placeholder="Buscar produtos..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-56 px-4 py-2 pl-10 bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-500 focus:outline-none focus:border-red-500 transition-colors text-sm" data-testid="search-input" />
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-zinc-500" />
              </div>
            </form>

            {isAuthenticated && (
              <Link to="/favoritos" className="p-2 text-white hover:text-red-500 transition-colors" data-testid="nav-favorites">
                <Heart className="h-5 w-5" />
              </Link>
            )}

            <Link to="/carrinho" className="relative p-2 text-white hover:text-red-500 transition-colors" data-testid="nav-cart">
              <ShoppingCart className="h-5 w-5" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-bold rounded-full h-5 w-5 flex items-center justify-center" data-testid="cart-count">{itemCount}</span>
              )}
            </Link>

            {isAuthenticated ? (
              <div className="relative">
                <button onClick={(e) => { e.stopPropagation(); setUserMenuOpen(!userMenuOpen); }}
                  className="flex items-center gap-2 p-1.5 rounded-full hover:bg-zinc-800 transition-colors" data-testid="user-menu-btn">
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
                  ) : (
                    <User className="h-5 w-5 text-white" />
                  )}
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 top-12 bg-zinc-900 border border-zinc-800 rounded-lg shadow-xl py-2 w-48 z-50" onClick={(e) => e.stopPropagation()}>
                    <p className="px-4 py-2 text-sm text-zinc-400 border-b border-zinc-800">{user?.name}</p>
                    <Link to="/favoritos" className="flex items-center gap-2 px-4 py-2 text-sm text-white hover:bg-zinc-800" onClick={() => setUserMenuOpen(false)}>
                      <Heart className="h-4 w-4" /> Favoritos
                    </Link>
                    <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 text-sm text-red-400 hover:bg-zinc-800 w-full text-left" data-testid="logout-btn">
                      <LogOut className="h-4 w-4" /> Sair
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="hidden md:flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm font-bold transition-colors" data-testid="nav-login">
                <User className="h-4 w-4" /> Entrar
              </Link>
            )}

            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 text-white hover:text-red-500 transition-colors" data-testid="mobile-menu-btn">
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-zinc-900 border-t border-zinc-800">
          <form onSubmit={handleSearch} className="px-4 pt-4">
            <div className="relative">
              <input type="text" placeholder="Buscar produtos..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-3 pl-10 bg-zinc-800 border border-zinc-700 text-white placeholder-zinc-500 focus:outline-none focus:border-red-500" data-testid="mobile-search-input" />
              <Search className="absolute left-3 top-3.5 h-5 w-5 text-zinc-500" />
            </div>
          </form>
          <nav className="px-4 py-4 space-y-1">
            <Link to="/" onClick={() => setMobileMenuOpen(false)} className="block text-white hover:text-red-500 font-semibold py-2 uppercase text-sm tracking-wider">Início</Link>
            <Link to="/produtos" onClick={() => setMobileMenuOpen(false)} className="block text-white hover:text-red-500 font-semibold py-2 uppercase text-sm tracking-wider">Produtos</Link>
            <Link to="/produtos?promocao=true" onClick={() => setMobileMenuOpen(false)} className="block text-white hover:text-red-500 font-semibold py-2 uppercase text-sm tracking-wider">Promoções</Link>
            {!isAuthenticated && (
              <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="block text-red-500 hover:text-red-400 font-semibold py-2 uppercase text-sm tracking-wider">Entrar</Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
